<script setup>
import { ref, computed } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Props
const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  selectedOption: {
    type: Object,
    default: null
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['select']);

// Methods
const handleBlockClick = (option) => {
  // Se clicar no mesmo bloco já selecionado, desseleciona
  if (props.selectedOption && props.selectedOption.name === option.name) {
    emit('select', null);
  } else {
    emit('select', option);
  }
};

const isSelected = (option) => {
  return props.selectedOption && props.selectedOption.name === option.name;
};
</script>

<template>
  <div class="adjustment-blocks">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-16 gap-4">
      <Spinner :size="40" />
      <p class="text-n-slate-11 text-sm animate-pulse">
        Gerando sugestões de ajuste...
      </p>
    </div>
    
    <!-- Options Grid -->
    <div v-else-if="options.length > 0" class="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="group border relative p-6 rounded-lg cursor-pointer transition-all duration-200"
        :class="{
          'ring-2 ring-n-brand border-n-brand': isSelected(option),
          'hover:border-n-slate-6 border-n-slate-3 bg-white dark:bg-slate-900': !isSelected(option)
        }"
        @click="handleBlockClick(option)">
        
        <!-- Card Header -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                 :class="{
                   'bg-n-brand/10 text-n-brand': isSelected(option),
                   'bg-n-slate-1 text-n-slate-11 group-hover:bg-n-slate-2': !isSelected(option)
                 }">
              <i v-if="index === 0" class="i-lucide-settings text-lg"></i>
              <i v-else-if="index === 1" class="i-lucide-sparkles text-lg"></i>
              <i v-else class="i-lucide-zap text-lg"></i>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium px-2 py-1 rounded-full"
                    :class="{
                      'bg-n-brand/10 text-n-brand': isSelected(option),
                      'bg-n-slate-1 text-n-slate-11': !isSelected(option)
                    }">
                Sugestão {{ index + 1 }}
              </span>
            </div>
          </div>
          
          <!-- Selection Indicator -->
          <div v-if="isSelected(option)" 
               class="flex-shrink-0 w-6 h-6 rounded-full bg-n-brand flex items-center justify-center">
            <i class="i-lucide-check text-white text-sm"></i>
          </div>
        </div>
        
        <!-- Content -->
        <div class="space-y-3">
          <h3 class="text-base font-semibold leading-tight text-n-slate-12">
            {{ option.name }}
          </h3>
          
          <!-- Visual hint of content -->
          <div class="space-y-2">
            <div class="h-2 bg-n-slate-2 rounded w-full"></div>
            <div class="h-2 bg-n-slate-2 rounded w-4/5"></div>
            <div class="h-2 bg-n-slate-2 rounded w-3/5"></div>
          </div>
          
          <div class="pt-3 pl-2 mt-3 border-t border-n-slate-3">
            <span class="text-xs font-medium flex items-center gap-1 cursor-pointer transition-colors"
                  :class="{
                    'text-n-brand': isSelected(option),
                    'text-n-slate-11': !isSelected(option)
                  }">
              <i class="i-lucide-mouse-pointer mr-1"></i>
              Clique para {{ isSelected(option) ? 'desselecionar' : 'selecionar' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-16 px-8 text-center">
      <div class="w-16 h-16 mb-4 bg-n-slate-1 rounded-full flex items-center justify-center">
        <i class="i-lucide-sparkles text-2xl text-n-slate-11"></i>
      </div>
      <p class="text-n-slate-11 text-base">
        Nenhuma opção de ajuste disponível.
      </p>
      <p class="text-n-slate-10 text-sm mt-1">
        Selecione um agente para ver as sugestões
      </p>
    </div>
  </div>
</template>

<style scoped>
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>